import dayjs from 'dayjs';
import type { UIField } from '../types/applicationSettings';

// ── Evaluation Context ────────────────────────────────────────────────────────

export interface EvalContext {
  current: Record<string, string | number | null>;
  P: Record<string, string | number | null>;
  D: Record<string, string | number | null>;
  M: Record<string, string | number | null>;
  Y: Record<string, string | number | null>;
}

type HistoryEntry = { date: string; value: unknown };

function isHistoryArray(v: unknown): v is HistoryEntry[] {
  return Array.isArray(v) && v.length > 0 && typeof (v[0] as HistoryEntry).date === 'string';
}

function extractScalar(v: unknown): string | number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return v;
  return String(v);
}

function latestEntryAtOrBefore(history: HistoryEntry[], cutoff: string): string | number | null {
  const sorted = [...history].sort((a, b) => b.date.localeCompare(a.date));
  const entry = sorted.find((e) => e.date <= cutoff);
  return entry ? extractScalar(entry.value) : null;
}

export function buildEvalContext(
  record: Record<string, unknown>,
  formValues: Record<string, string>,
  selectedDate: string,
  sortedHistoryDates: string[],
): EvalContext {
  const ctx: EvalContext = { current: {}, P: {}, D: {}, M: {}, Y: {} };

  const selectedIdx = sortedHistoryDates.indexOf(selectedDate);
  const prevEntryDate =
    selectedIdx >= 0 && selectedIdx + 1 < sortedHistoryDates.length
      ? sortedHistoryDates[selectedIdx + 1]
      : null;

  const prevDayCutoff = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
  const prevMonthEnd = dayjs(selectedDate).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
  const prevYearEnd = dayjs(selectedDate).subtract(1, 'year').endOf('year').format('YYYY-MM-DD');

  for (const [fieldName, rawValue] of Object.entries(record)) {
    ctx.current[fieldName] = formValues[fieldName] !== undefined
      ? (formValues[fieldName] || null)
      : extractScalar(rawValue);

    if (isHistoryArray(rawValue)) {
      ctx.P[fieldName] = prevEntryDate
        ? extractScalar(rawValue.find((e) => e.date === prevEntryDate)?.value)
        : null;
      ctx.D[fieldName] = latestEntryAtOrBefore(rawValue, prevDayCutoff);
      ctx.M[fieldName] = latestEntryAtOrBefore(rawValue, prevMonthEnd);
      ctx.Y[fieldName] = latestEntryAtOrBefore(rawValue, prevYearEnd);
    } else {
      ctx.P[fieldName] = null;
      ctx.D[fieldName] = null;
      ctx.M[fieldName] = null;
      ctx.Y[fieldName] = null;
    }
  }

  for (const [fieldName, val] of Object.entries(formValues)) {
    if (!(fieldName in ctx.current)) {
      ctx.current[fieldName] = val || null;
    }
  }

  return ctx;
}

// ── Formula Substitution ──────────────────────────────────────────────────────

const FIELD_REF_RE = /([PDMY]?)\{([^}]+)\}/g;

function resolveRef(prefix: string, fieldName: string, ctx: EvalContext): string | number | null {
  const bucket = prefix === '' ? ctx.current : ctx[prefix as 'P' | 'D' | 'M' | 'Y'];
  return bucket[fieldName] ?? null;
}

interface SubResult { expr: string; hasMissing: boolean }

function substituteRefs(formula: string, ctx: EvalContext): SubResult {
  let hasMissing = false;
  const expr = formula.replace(FIELD_REF_RE, (_, prefix: string, fieldName: string) => {
    const val = resolveRef(prefix, fieldName, ctx);
    if (val === null || val === undefined) { hasMissing = true; return '__MISSING__'; }
    const cleaned = typeof val === 'string' ? val.replace(/,/g, '') : String(val);
    const num = Number(cleaned);
    if (!isNaN(num) && cleaned.trim() !== '') return String(num);
    return '"' + String(val).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  });
  return { expr, hasMissing };
}

// ── Tokenizer ─────────────────────────────────────────────────────────────────

type TokenType = 'NUM' | 'STR' | 'OP' | 'CMP' | 'LPAREN' | 'RPAREN' | 'IDENT' | 'COMMA' | 'EOF';
interface Token { type: TokenType; value: string }

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    if (/\s/.test(expr[i])) { i++; continue; }

    if (/[0-9]/.test(expr[i]) || (expr[i] === '.' && /[0-9]/.test(expr[i + 1] ?? ''))) {
      let num = '';
      while (i < expr.length && /[0-9.]/.test(expr[i])) num += expr[i++];
      tokens.push({ type: 'NUM', value: num });
      continue;
    }

    if (expr[i] === '"') {
      let str = '';
      i++;
      while (i < expr.length && expr[i] !== '"') {
        if (expr[i] === '\\') { i++; str += expr[i++] ?? ''; }
        else str += expr[i++];
      }
      i++;
      tokens.push({ type: 'STR', value: str });
      continue;
    }

    if (/[A-Za-z_]/.test(expr[i])) {
      let id = '';
      while (i < expr.length && /[A-Za-z0-9_]/.test(expr[i])) id += expr[i++];
      tokens.push({ type: 'IDENT', value: id });
      continue;
    }

    // Multi-char comparison operators first
    if (expr[i] === '>' && expr[i + 1] === '=') { tokens.push({ type: 'CMP', value: '>=' }); i += 2; continue; }
    if (expr[i] === '<' && expr[i + 1] === '=') { tokens.push({ type: 'CMP', value: '<=' }); i += 2; continue; }
    if (expr[i] === '!' && expr[i + 1] === '=') { tokens.push({ type: 'CMP', value: '!=' }); i += 2; continue; }
    if (expr[i] === '>') { tokens.push({ type: 'CMP', value: '>' }); i++; continue; }
    if (expr[i] === '<') { tokens.push({ type: 'CMP', value: '<' }); i++; continue; }
    if (expr[i] === '=') { tokens.push({ type: 'CMP', value: '=' }); i++; continue; }

    if ('+-*/'.includes(expr[i])) { tokens.push({ type: 'OP', value: expr[i++] }); continue; }
    if (expr[i] === '(') { tokens.push({ type: 'LPAREN', value: '(' }); i++; continue; }
    if (expr[i] === ')') { tokens.push({ type: 'RPAREN', value: ')' }); i++; continue; }
    if (expr[i] === ',') { tokens.push({ type: 'COMMA', value: ',' }); i++; continue; }

    i++;
  }
  tokens.push({ type: 'EOF', value: '' });
  return tokens;
}

// ── Recursive-Descent Parser ──────────────────────────────────────────────────

class Parser {
  private tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) { this.tokens = tokens; }

  private peek(): Token { return this.tokens[this.pos] ?? { type: 'EOF', value: '' }; }
  private consume(): Token { return this.tokens[this.pos++] ?? { type: 'EOF', value: '' }; }

  parse(): number | string {
    const val = this.parseAdditive();
    if (this.peek().type !== 'EOF') throw new Error('Unexpected token: ' + this.peek().value);
    return val;
  }

  private parseAdditive(): number | string {
    let left = this.parseMultiplicative();
    while (this.peek().type === 'OP' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.consume().value;
      const right = this.parseMultiplicative();
      if (op === '+') {
        left = (typeof left === 'string' || typeof right === 'string')
          ? String(left) + String(right)
          : (left as number) + (right as number);
      } else {
        left = (left as number) - (right as number);
      }
    }
    return left;
  }

  private parseMultiplicative(): number | string {
    let left = this.parseUnary();
    while (this.peek().type === 'OP' && (this.peek().value === '*' || this.peek().value === '/')) {
      const op = this.consume().value;
      const right = this.parseUnary();
      if (op === '*') left = (left as number) * (right as number);
      else {
        if ((right as number) === 0) throw new Error('Division by zero');
        left = (left as number) / (right as number);
      }
    }
    return left;
  }

  private parseUnary(): number | string {
    if (this.peek().type === 'OP' && this.peek().value === '-') {
      this.consume();
      return -(this.parsePrimary() as number);
    }
    return this.parsePrimary();
  }

  private parsePrimary(): number | string {
    const tok = this.peek();

    if (tok.type === 'NUM') { this.consume(); return parseFloat(tok.value); }
    if (tok.type === 'STR') { this.consume(); return tok.value; }

    if (tok.type === 'LPAREN') {
      this.consume();
      const val = this.parseAdditive();
      if (this.peek().type !== 'RPAREN') throw new Error('Expected )');
      this.consume();
      return val;
    }

    if (tok.type === 'IDENT') {
      this.consume();
      const fnName = tok.value.toUpperCase();
      if (this.peek().type !== 'LPAREN') throw new Error('Expected ( after ' + fnName);
      this.consume();
      const args = this.parseArgList();
      if (this.peek().type !== 'RPAREN') throw new Error('Expected ) after arguments');
      this.consume();

      switch (fnName) {
        case 'ROUND':  return Math.round(args[0] as number);
        case 'CEIL':   return Math.ceil(args[0] as number);
        case 'FLOOR':  return Math.floor(args[0] as number);
        case 'ABS':    return Math.abs(args[0] as number);
        case 'MOD': {
          if ((args[1] as number) === 0) throw new Error('Division by zero');
          return (args[0] as number) % (args[1] as number);
        }
        case 'CONCAT': return args.map(String).join('');
        default: throw new Error('Unknown function: ' + fnName);
      }
    }

    throw new Error('Unexpected token: ' + tok.value);
  }

  private parseArgList(): (number | string)[] {
    const args: (number | string)[] = [];
    if (this.peek().type === 'RPAREN') return args;
    args.push(this.parseAdditive());
    while (this.peek().type === 'COMMA') {
      this.consume();
      args.push(this.parseAdditive());
    }
    return args;
  }
}

function evalExpression(expr: string): number | string {
  return new Parser(tokenize(expr)).parse();
}

// ── DSL AST ───────────────────────────────────────────────────────────────────

type Stmt =
  | { kind: 'value'; expr: string }
  | { kind: 'if'; branches: { condition: string; body: Stmt[] }[]; elseBranch?: Stmt[] };

function isControlFlow(line: string): boolean {
  const lower = line.toLowerCase();
  return lower === 'else'
    || lower === 'end if'
    || lower === 'endif'
    || lower.startsWith('else if ');
}

function parseBlock(lines: string[], startIdx: number): { stmts: Stmt[]; nextIdx: number } {
  const stmts: Stmt[] = [];
  let i = startIdx;
  while (i < lines.length) {
    const line = lines[i];
    if (isControlFlow(line)) break;
    if (line.toLowerCase().startsWith('if ')) {
      const { stmt, nextIdx } = parseIf(lines, i);
      stmts.push(stmt);
      i = nextIdx;
    } else {
      stmts.push({ kind: 'value', expr: line });
      i++;
    }
  }
  return { stmts, nextIdx: i };
}

function parseIf(lines: string[], startIdx: number): { stmt: Stmt; nextIdx: number } {
  let i = startIdx;
  const branches: { condition: string; body: Stmt[] }[] = [];
  let foundEndIf = false;
  let elseBranch: Stmt[] | undefined;

  const firstCond = lines[i].slice(3).trim();
  i++;
  const { stmts: firstBody, nextIdx: afterFirst } = parseBlock(lines, i);
  branches.push({ condition: firstCond, body: firstBody });
  i = afterFirst;

  while (i < lines.length) {
    const lower = lines[i].toLowerCase();
    if (lower.startsWith('else if ')) {
      const cond = lines[i].slice(8).trim();
      i++;
      const { stmts: body, nextIdx: after } = parseBlock(lines, i);
      branches.push({ condition: cond, body });
      i = after;
    } else if (lower === 'else') {
      i++;
      const { stmts: body, nextIdx: after } = parseBlock(lines, i);
      elseBranch = body;
      i = after;
      if (i < lines.length && (lines[i].toLowerCase() === 'end if' || lines[i].toLowerCase() === 'endif')) {
        i++;
        foundEndIf = true;
      }
      break;
    } else if (lower === 'end if' || lower === 'endif') {
      i++;
      foundEndIf = true;
      break;
    } else {
      break;
    }
  }

  if (!foundEndIf) throw new Error("Unclosed 'if' block — missing 'end if'");

  return { stmt: { kind: 'if', branches, elseBranch }, nextIdx: i };
}

// Exported for save-time validation in the settings page
export function parseDSL(code: string): Stmt[] {
  const lines = code.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  const { stmts, nextIdx } = parseBlock(lines, 0);
  if (nextIdx < lines.length) {
    throw new Error(`Unexpected keyword: '${lines[nextIdx]}'`);
  }
  return stmts;
}

// ── Condition Evaluator ───────────────────────────────────────────────────────

function evaluateCondition(condStr: string, ctx: EvalContext): boolean {
  const { expr, hasMissing } = substituteRefs(condStr.trim(), ctx);
  if (hasMissing) return false;

  const tokens = tokenize(expr);
  let depth = 0;
  let cmpIdx = -1;
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].type === 'LPAREN') depth++;
    else if (tokens[i].type === 'RPAREN') depth--;
    else if (tokens[i].type === 'CMP' && depth === 0) { cmpIdx = i; break; }
  }
  if (cmpIdx === -1) throw new Error('No comparison operator in condition: ' + condStr);

  const op = tokens[cmpIdx].value;
  const leftTokens = [...tokens.slice(0, cmpIdx), { type: 'EOF' as const, value: '' }];
  const rightTokens = tokens.slice(cmpIdx + 1);

  const left = new Parser(leftTokens).parse();
  const right = new Parser(rightTokens).parse();

  const leftN = typeof left === 'number' ? left : Number(String(left).replace(/,/g, ''));
  const rightN = typeof right === 'number' ? right : Number(String(right).replace(/,/g, ''));
  const bothNumeric = !isNaN(leftN) && !isNaN(rightN);

  if (bothNumeric) {
    switch (op) {
      case '>':  return leftN > rightN;
      case '<':  return leftN < rightN;
      case '>=': return leftN >= rightN;
      case '<=': return leftN <= rightN;
      case '=':  return leftN === rightN;
      case '!=': return leftN !== rightN;
    }
  }
  switch (op) {
    case '=':  return String(left) === String(right);
    case '!=': return String(left) !== String(right);
    default:   return false;
  }
}

// ── DSL Executor ─────────────────────────────────────────────────────────────

function evaluateValueLine(expr: string, ctx: EvalContext): string | null {
  try {
    const { expr: substituted, hasMissing } = substituteRefs(expr.trim(), ctx);
    if (hasMissing) return null;
    return String(evalExpression(substituted));
  } catch {
    return '!Error';
  }
}

function executeStmt(stmt: Stmt, ctx: EvalContext): string | null {
  if (stmt.kind === 'value') {
    return evaluateValueLine(stmt.expr, ctx);
  }
  for (const branch of stmt.branches) {
    if (evaluateCondition(branch.condition, ctx)) {
      return executeStmts(branch.body, ctx) ?? '';
    }
  }
  if (stmt.elseBranch) {
    return executeStmts(stmt.elseBranch, ctx) ?? '';
  }
  return null;
}

function executeStmts(stmts: Stmt[], ctx: EvalContext): string | null {
  let result: string | null = null;
  for (const stmt of stmts) {
    const r = executeStmt(stmt, ctx);
    if (r === '!Error') return '!Error';
    if (r !== null) result = r;
  }
  return result;
}

export function evaluateDSL(code: string, ctx: EvalContext): string {
  try {
    const trimmed = code.trim();
    if (!trimmed) return '';
    const stmts = parseDSL(trimmed);
    if (stmts.length === 0) return '';
    const result = executeStmts(stmts, ctx);
    if (result === '!Error') return '!Error';
    return result ?? 'N/A';
  } catch {
    return '!Error';
  }
}

// Keep for any internal use; UIField import satisfies the type reference
export type { UIField };
